import { FlamingoTypes, RangeValueOption, ReportTypes } from '@mode/shared/contract-common';
import { CompiledField } from '@mode/shared/util-switch';
import * as angular from 'angular';

import { EMap } from '@mode/reports/sandbox/contract';
import { ImmutableRecordSet } from '@mode/legacy/types-app-state';
import {
  MultiValueFilterControl,
  MultiValueSelection,
  RangeValueFilterControl,
  LegacyReportFilterControl,
  SingleValueFilterControl,
  SingleValueSelection,
} from '@mode/filters/util';
import { MAX_SELECTED_FILTER_OPTS } from '@mode/shared/contract-common';
import { Set } from 'immutable';
import { AppStore } from '~/types/main.types';

export default class FilterPanelController implements angular.IController {
  static $inject = ['$element', 'FeatureFlagsFacade', 'RxStoreService'];

  filters: LegacyReportFilterControl[];
  filterOpts: EMap<ReportTypes.ReportFilterOptions>;
  optCounts: EMap<number>;
  selectedOptCounts: EMap<number>;
  model: FlamingoTypes.ModeModel;
  isCalcByFilterId: { [id: string]: boolean }; // dictionary that stores whether a filter is on a calculated field
  uiFilters: ImmutableRecordSet<LegacyReportFilterControl>;
  showEmptyState = false;
  loading = false;

  discreteFilterOptSets: { [token: string]: Set<ReportTypes.ReportFilterValue> };
  currentMultiOptCounts: { [token: string]: number }; // the realtime selected counts for multi filters

  calcFieldTooltip = `Filters created from calculated fields won't filter custom visualizations, notebook output, and legacy visualizations, which include Pivot Tables, Big Numbers, and Display Tables.`;

  orphanedFilterTooltip = `This filter is no longer valid. Try deleting the filter then adding it again.`;

  tooManySelectionsTooltip = `Current filter configuration might create chart errors due to a long filter query. Try to limit filter selection, use exclude filter or select all.`;

  onPanelClose: () => void;
  onFiltersApply: ($args: { $filters: ReadonlyArray<LegacyReportFilterControl> }) => void;
  onFiltersReset: ($args: { $filters: ReadonlyArray<LegacyReportFilterControl> }) => void;
  onFiltersSetDefault: ($args: { $filters: ReadonlyArray<LegacyReportFilterControl> }) => void;

  onFilterReset: ($args: { $filter: LegacyReportFilterControl }) => void;
  onFilterDelete: ($args: { $filter: LegacyReportFilterControl }) => void;
  onFilterMoveToBottom: ($args: { $filter: LegacyReportFilterControl }) => void;
  onFilterMoveToPosition: ($args: {
    $filter: LegacyReportFilterControl;
    $nextFilter: LegacyReportFilterControl;
  }) => void;

  onFilterSetType: ($args: {
    $filter: LegacyReportFilterControl;
    $filterType: ReportTypes.ReportFilterType;
    $applyFilters: boolean;
    $defaultSelection: any;
  }) => void;

  onFilterSetRangeIncludeNulls: ($args: { $filter: RangeValueFilterControl; $value: boolean }) => void;
  onFilterSetSelectControlType: ($args: {
    $filter: SingleValueFilterControl | MultiValueFilterControl;
    $controlType: ReportTypes.SelectControlType;
  }) => void;

  onSingleSelection: ($args: { $filter: SingleValueFilterControl; $value: SingleValueSelection }) => void;
  onMultiSelection: ($args: { $filter: MultiValueFilterControl; $value: MultiValueSelection }) => void;
  onRangeSelection: ($args: { $filter: RangeValueFilterControl; $value: RangeValueOption }) => void;

  onFilterHideSelectAll: ($args: {
    $filter: SingleValueFilterControl | MultiValueFilterControl;
    $hideSelectAll: boolean;
    $defaultSelection: any;
  }) => void;

  constructor(
    private $element: JQuery,
    public rxStore: AppStore
  ) {
    this.uiFilters = new ImmutableRecordSet<LegacyReportFilterControl>('token');
  }

  $onInit() {
    this.isCalcByFilterId = {};
  }

  $onChanges(changes: angular.IOnChangesObject): void {
    if (changes.filters) {
      const multiFilters = this.filters.filter(
        (filter): filter is MultiValueFilterControl => filter.filterType === ReportTypes.ReportFilterType.Multi
      );
      this.uiFilters = new ImmutableRecordSet<LegacyReportFilterControl>('token').mergeRecords(this.filters);

      if (this.model != null) {
        this.isCalcByFilterId = {};
        this.filters.forEach((filter) => {
          this.isCalcByFilterId[filter.token] = this.model.views.some((view) =>
            CompiledField.formulaHasCalcAttr(view, filter.formula)
          );
        });
      }

      this.currentMultiOptCounts = multiFilters.reduce(
        (counts, filter) => {
          counts[filter.token] = filter.selected.values.size;
          return counts;
        },
        {} as { [token: string]: number }
      );
    }
    if (!this.filters.length) {
      this.showEmptyState = true;
    }

    if (changes.filterOpts && this.filterOpts != null) {
      this.discreteFilterOptSets = Object.keys(this.filterOpts).reduce(
        (sets, token) => {
          const opts = this.filterOpts[token];
          if (opts.filterType !== ReportTypes.ReportFilterType.Range) {
            sets[token] = Set(opts.values.map((v) => v.value));
          }

          return sets;
        },
        {} as { [token: string]: Set<ReportTypes.ReportFilterValue> }
      );
    }
  }

  handleSingleSelection(filter: SingleValueFilterControl, selection: ReportTypes.DiscreteFilterSelectionTypes): void {
    const record = this.uiFilters.getRecord(filter.token);
    if (record.filterType === ReportTypes.ReportFilterType.Single) {
      if (selection.type === ReportTypes.DiscreteSelectionType.Select) {
        this.uiFilters = this.uiFilters.mergeRecord(
          record.with({
            selected: record.selected.with({ value: selection.value, all: false }),
          })
        );
      } else {
        const all = selection.type === ReportTypes.DiscreteSelectionType.All;
        this.uiFilters = this.uiFilters.mergeRecord(
          record.with({
            selected: record.selected.with({ value: null, all }),
          })
        );
      }
    }
  }

  handleMultiSelection(filter: MultiValueFilterControl, selection: ReportTypes.DiscreteFilterSelectionTypes): void {
    const record = this.uiFilters.getRecord(filter.token);
    if (record.filterType === ReportTypes.ReportFilterType.Multi) {
      if (selection.type === ReportTypes.DiscreteSelectionType.Select) {
        let selected = record.selected; // this is the selected options before the user's click

        if (selected.all) {
          // need to check all options first in order to uncheck a single option when showing an "all" state.
          // Assumed it's an uncheck operation because it's not possible to do anything else if all is checked
          selected = selected.with({
            values: this.discreteFilterOptSets[filter.token].remove(selection.value),
            all: false,
          });
        } else {
          selected = selected.with({
            values: selection.selected ? selected.values.add(selection.value) : selected.values.remove(selection.value),
          });

          /**
           * Transitional logic. For performance reasons, in the future most of this logic will be done
           * in the parent app because the iframe won't be receiving all the filter options.
           * For now, we'll be doing it here, but some steps will be taken to ensure an easier
           * transition to a version where only visible (i.e. first 250 or 1000) filter options
           * are provided to the iframe.
           */
          const totalCount = this.optCounts[filter.token]; // Total number of options in the domain
          const selectedCount = this.selectedOptCounts[filter.token]; // Count of selected options sent in the last Flamingo query
          const currentOptCount = selected.values.count(); // Count of selected options as seen by the user in the filter panel
          const embedOptCount = filter.selected.values.count(); // Count of selected options as received by the iframe (i.e. out of the max 250 given, N selected)

          /**
           * Check if all should be checked. The theory is that we should be able to determine
           * if all the options are selected given we know how many more options we are selecting,
           * which we can add to the total number of selected options to see what the new selected
           * total should be.
           */
          if (totalCount === selectedCount + currentOptCount - embedOptCount) {
            selected = selected.with({
              all: true,
              values: selected.values.clear(),
            });
          }
        }

        this.currentMultiOptCounts[filter.token] = selected.values.size;
        this.uiFilters = this.uiFilters.mergeRecord(
          record.with({
            selected,
          })
        );
      } else {
        const all = selection.type === ReportTypes.DiscreteSelectionType.All;
        this.uiFilters = this.uiFilters.mergeRecord(
          record.with({
            selected: record.selected.with({ values: record.selected.values.clear(), all }),
          })
        );
      }
    }
  }

  handleRangeSelection(filter: RangeValueFilterControl, selected: RangeValueOption): void {
    this.uiFilters = this.uiFilters.mergeRecords([filter.with({ selected })]);
  }

  // UI

  get isDirty(): boolean {
    return this.filters.some((f) => this.isDirtyFilter(f));
  }

  hasTooManySelected(filterToken: string) {
    return this.currentMultiOptCounts[filterToken] > MAX_SELECTED_FILTER_OPTS;
  }

  close() {
    this.onPanelClose();
  }

  // All Filters

  apply(ev: Event): void {
    ev.preventDefault();
    if (!this.loading) {
      this.onFiltersApply({
        $filters: this.uiFilters.getRecords(),
      });
    }
  }

  reset(ev: Event): void {
    ev.preventDefault();
    this.onFiltersReset({
      $filters: this.uiFilters.getRecords(),
    });
  }

  setDefault(ev: Event): void {
    ev.preventDefault();
    if (this.isDirty) {
      this.onFiltersSetDefault({
        $filters: this.uiFilters.getRecords(),
      });
    }
  }

  // Individual Filters

  isDirtyFilter(filter: LegacyReportFilterControl): boolean {
    const uiFilter = this.getFilter(filter);

    switch (uiFilter.filterType) {
      case ReportTypes.ReportFilterType.Single:
        return uiFilter.selected.all !== uiFilter.default.all || uiFilter.selected.value !== uiFilter.default.value;
      case ReportTypes.ReportFilterType.Multi:
        return (
          uiFilter.selected.all !== uiFilter.default.all || !uiFilter.selected.values.equals(uiFilter.default.values)
        );
      case ReportTypes.ReportFilterType.Range:
        return uiFilter.selected.min !== uiFilter.default.min || uiFilter.selected.max !== uiFilter.default.max;
      default:
        return false;
    }
  }

  isTopFilter(filter: LegacyReportFilterControl): boolean {
    if (this.uiFilters.size === 0) {
      return false;
    }

    return filter === this.getTopFilter();
  }

  isBottomFilter(filter: LegacyReportFilterControl): boolean {
    if (this.uiFilters.size === 0) {
      return false;
    }

    return filter === this.getBottomFilter();
  }

  handleFilterReset(filter: LegacyReportFilterControl): void {
    this.onFilterReset({
      $filter: this.getFilter(filter),
    });
  }

  handleFilterDelete(filter: LegacyReportFilterControl): void {
    this.onFilterDelete({
      $filter: this.getFilter(filter),
    });
  }

  handleFilterMoveToBottom(filter: LegacyReportFilterControl): void {
    this.onFilterMoveToBottom({ $filter: filter });
  }

  handleFilterMoveToTop(filter: LegacyReportFilterControl): void {
    const topFilter = this.getTopFilter();

    if (topFilter !== null && topFilter !== filter) {
      this.onFilterMoveToPosition({ $filter: filter, $nextFilter: topFilter });
    }
  }

  handleFilterMoveUp(filter: LegacyReportFilterControl): void {
    const prevFilter = this.getPrevFilter(filter);

    if (prevFilter !== null) {
      this.onFilterMoveToPosition({ $filter: filter, $nextFilter: prevFilter });
    }
  }

  handleFilterMoveDown(filter: LegacyReportFilterControl): void {
    const nextFilter = this.getNextFilter(filter);

    if (nextFilter !== null) {
      this.onFilterMoveToPosition({ $filter: nextFilter, $nextFilter: filter });
    }
  }

  handleFilterSetType(filter: LegacyReportFilterControl, type: ReportTypes.ReportFilterType): void {
    // We need to update the default filter selection if
    // the filter is becoming a SingleSelect and hideSelectAll is true
    // and the filter is in selection mode all
    // todo: do we also need to update if there is more than 1 selection?
    let defaultSelection: ReportTypes.FilterSelection;
    let applyFilters = false;
    if (filter.hideSelectAll && type === ReportTypes.ReportFilterType.Single) {
      const selected = filter.selected as MultiValueSelection;
      const opts = this.filterOpts[filter.token];
      if (selected.all && opts) {
        const option = opts.values[0] as ReportTypes.DiscreteValueOption;
        const record = this.uiFilters.getRecord(filter.token) as MultiValueFilterControl;
        let selected = record.selected;
        // clear all filters
        selected.values.clear();
        // then select just the one we want
        selected = selected.with({
          values: selected.values.add(option.value),
          all: false,
        });
        this.uiFilters = this.uiFilters.mergeRecord(
          record.with({
            selected,
          })
        );
        this.onFiltersApply({
          $filters: this.uiFilters.getRecords(),
        });
        defaultSelection = {
          all: false,
          value: option.value,
        };
        applyFilters = true;
      }
    } else {
      switch (type) {
        case ReportTypes.ReportFilterType.Single:
          applyFilters = !(filter.selected as SingleValueSelection).all;
          defaultSelection = {
            all: true,
            value: null,
          };
          break;
        case ReportTypes.ReportFilterType.Multi:
          applyFilters = !(filter.selected as MultiValueSelection).all;
          defaultSelection = {
            all: true,
            values: [],
          };
          break;
      }
    }

    this.onFilterSetType({
      $filter: this.getFilter(filter),
      $filterType: type,
      $defaultSelection: defaultSelection,
      $applyFilters: applyFilters,
    });
  }

  handleSetHideSelectAll(filter: SingleValueFilterControl | MultiValueFilterControl): void {
    // Since initial 'null' value and 'false' both result in a visible
    // input, the very first click should hide the input. Otherwise,
    // toggle the value.
    const hideSelectAll = filter.hideSelectAll === null ? true : !filter.hideSelectAll;
    let newSelection;

    if (hideSelectAll && filter.filterType === ReportTypes.ReportFilterType.Single) {
      // Since select all appears as the selected list option, we need
      // to set a new option when select all disappears
      const selected = filter.selected as SingleValueSelection;
      const opts = this.filterOpts[filter.token];
      if (selected.all && opts) {
        const option = opts.values[0] as ReportTypes.DiscreteValueOption;
        const defaultSelection: ReportTypes.DiscreteFilterSelectionTypes = {
          type: ReportTypes.DiscreteSelectionType.Select,
          value: option.value,
          selected: true,
        };
        this.handleSingleSelection(filter, defaultSelection);
        this.onFiltersApply({
          $filters: this.uiFilters.getRecords(),
        });
        newSelection = this.getFilter(filter).selected.toObject();
      }
    }

    this.onFilterHideSelectAll({
      $filter: filter,
      $hideSelectAll: hideSelectAll,
      $defaultSelection: newSelection,
    });
  }

  handleFilterSetSelectControlType(
    filter: SingleValueFilterControl | MultiValueFilterControl,
    type: ReportTypes.SelectControlType
  ): void {
    switch (filter.filterType) {
      case ReportTypes.ReportFilterType.Single:
        this.onFilterSetSelectControlType({
          $filter: this.getSingleValueFilter(filter),
          $controlType: type,
        });
        break;
      case ReportTypes.ReportFilterType.Multi:
        this.onFilterSetSelectControlType({
          $filter: this.getMultiValueFilter(filter),
          $controlType: type,
        });
        break;
    }
  }

  handleFilterSetIncludeNulls(filter: RangeValueFilterControl, value: boolean): void {
    this.onFilterSetRangeIncludeNulls({
      $filter: this.getRangeValueFilterControl(filter),
      $value: value,
    });
  }

  showRangeFieldOption(filter: LegacyReportFilterControl): boolean {
    return filter.isNumericType || (filter.isDateType && !filter.isDiscreteDateType);
  }

  isFilterCalcField(filter: LegacyReportFilterControl): boolean {
    return this.isCalcByFilterId[filter.token] === true;
  }

  isSelectAllType(filter: LegacyReportFilterControl): boolean {
    return (
      filter.filterType === ReportTypes.ReportFilterType.Multi ||
      filter.filterType === ReportTypes.ReportFilterType.Single
    );
  }

  setSingleSelectBySelection(
    filter: SingleValueFilterControl,
    selection: ReportTypes.DiscreteFilterSelectionTypes
  ): void {
    const record = this.uiFilters.getRecord(filter.token);
    if (
      record.filterType === ReportTypes.ReportFilterType.Single &&
      selection.type === ReportTypes.DiscreteSelectionType.Select
    ) {
      this.uiFilters = this.uiFilters.mergeRecord(
        record.with({
          selected: record.selected.with({ value: selection.value, all: false }),
        })
      );
      this.onFiltersApply({
        $filters: this.uiFilters.getRecords(),
      });
    }
  }

  private getFilter(filter: LegacyReportFilterControl): LegacyReportFilterControl {
    if (this.uiFilters.hasRecord(filter.token)) {
      return this.uiFilters.getRecord(filter.token);
    } else {
      return filter;
    }
  }

  private getSingleValueFilter(filter: SingleValueFilterControl): SingleValueFilterControl {
    const uiFilter = this.getFilter(filter);
    return uiFilter.filterType === ReportTypes.ReportFilterType.Single ? uiFilter : filter;
  }

  private getMultiValueFilter(filter: MultiValueFilterControl): MultiValueFilterControl {
    const uiFilter = this.getFilter(filter);
    return uiFilter.filterType === ReportTypes.ReportFilterType.Multi ? uiFilter : filter;
  }

  private getRangeValueFilterControl(filter: RangeValueFilterControl): RangeValueFilterControl {
    const uiFilter = this.getFilter(filter);
    return uiFilter.filterType === ReportTypes.ReportFilterType.Range ? uiFilter : filter;
  }

  private getTopFilter(): LegacyReportFilterControl {
    return this.uiFilters.getRecord(this.uiFilters.keys.first());
  }

  private getBottomFilter(): LegacyReportFilterControl {
    return this.uiFilters.getRecord(this.uiFilters.keys.last());
  }

  private getNextFilter(filter: LegacyReportFilterControl): LegacyReportFilterControl {
    const records = this.uiFilters.getRecords();

    const index = records.indexOf(filter);
    if (index < 0 || index === records.length - 1) {
      return null;
    } else {
      return records[index + 1];
    }
  }

  private getPrevFilter(filter: LegacyReportFilterControl): LegacyReportFilterControl {
    const records = this.uiFilters.getRecords();

    const index = records.indexOf(filter);
    if (index < 0 || index === 0) {
      return null;
    } else {
      return records[index - 1];
    }
  }
}
